import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Loader2, User, CreditCard, FileText, Calendar, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ParentPortal = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('credit_card');

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      fetchPayments(selectedStudent.id);
      fetchDocuments(selectedStudent.id);
    }
  }, [selectedStudent]);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/parent/students`);
      setStudents(response.data);
      if (response.data.length > 0) {
        setSelectedStudent(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Erro ao carregar alunos');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async (studentId) => {
    try {
      const response = await axios.get(`${API}/parent/students/${studentId}/payments`);
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchDocuments = async (studentId) => {
    try {
      const response = await axios.get(`${API}/parent/students/${studentId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handlePayment = async (paymentId) => {
    try {
      await axios.post(`${API}/parent/students/${selectedStudent.id}/payments`, {
        amount: 350.00,
        payment_method: paymentMethod
      });
      toast.success('Pagamento realizado com sucesso!');
      fetchPayments(selectedStudent.id);
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Erro ao processar pagamento');
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    const documentType = e.target.document_type.value;
    const fileUrl = 'https://exemplo.com/documento.pdf'; // Mock URL
    
    try {
      await axios.post(
        `${API}/parent/students/${selectedStudent.id}/documents?document_type=${documentType}&file_url=${fileUrl}`
      );
      toast.success('Documento enviado com sucesso!');
      fetchDocuments(selectedStudent.id);
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Erro ao enviar documento');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin h-12 w-12 text-red-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Nenhum Aluno Cadastrado</h2>
              <p className="text-gray-600 mb-6">
                Você ainda não possui alunos cadastrados. Entre em contato com a administração.
              </p>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="parent-portal-title">
            Portal do Responsável
          </h1>
          <p className="text-gray-600">Bem-vindo(a), {user.name}</p>
        </div>

        {/* Student Selector */}
        {students.length > 1 && (
          <div className="mb-6">
            <Label>Selecionar Aluno</Label>
            <select 
              className="w-full md:w-auto mt-2 p-2 border rounded-md"
              value={selectedStudent?.id}
              onChange={(e) => {
                const student = students.find(s => s.id === e.target.value);
                setSelectedStudent(student);
              }}
            >
              {students.map(student => (
                <option key={student.id} value={student.id}>{student.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="info" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto">
            <TabsTrigger value="info" data-testid="info-tab">
              <User className="mr-2 h-4 w-4" />
              Informações
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="payments-tab">
              <CreditCard className="mr-2 h-4 w-4" />
              Mensalidades
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="documents-tab">
              <FileText className="mr-2 h-4 w-4" />
              Documentos
            </TabsTrigger>
          </TabsList>

          {/* Info Tab */}
          <TabsContent value="info">
            <Card>
              <CardHeader>
                <CardTitle>Informações do Aluno</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-600">Nome</Label>
                    <p className="text-lg font-semibold">{selectedStudent?.name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Data de Nascimento</Label>
                    <p className="text-lg font-semibold">{selectedStudent?.birth_date}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Categoria</Label>
                    <p className="text-lg font-semibold">{selectedStudent?.category}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Status</Label>
                    <p className="text-lg font-semibold capitalize">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                        selectedStudent?.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedStudent?.status === 'active' ? 'Ativo' : 'Inativo'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Data de Matrícula</Label>
                    <p className="text-lg font-semibold">
                      {selectedStudent?.enrollment_date ? new Date(selectedStudent.enrollment_date).toLocaleDateString('pt-BR') : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Mensalidades</CardTitle>
              </CardHeader>
              <CardContent>
                {payments.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Nenhuma mensalidade encontrada</p>
                ) : (
                  <div className="space-y-4">
                    {payments.map(payment => (
                      <Card key={payment.id} className="border">
                        <CardContent className="p-4">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                            <div className="mb-4 md:mb-0">
                              <div className="flex items-center mb-2">
                                <Calendar className="mr-2 h-4 w-4 text-gray-500" />
                                <span className="font-semibold">Vencimento: {payment.due_date}</span>
                              </div>
                              <p className="text-2xl font-bold text-red-600">
                                R$ {payment.amount.toFixed(2)}
                              </p>
                              <div className="mt-2">
                                {payment.status === 'paid' ? (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                                    <CheckCircle className="mr-1 h-4 w-4" />
                                    Pago em {payment.paid_date}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                                    <Clock className="mr-1 h-4 w-4" />
                                    Pendente
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {payment.status === 'pending' && (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button className="bg-red-600 hover:bg-red-700" data-testid="pay-button">
                                    Pagar Agora
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Simular Pagamento</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 py-4">
                                    <div>
                                      <Label>Valor</Label>
                                      <Input value={`R$ ${payment.amount.toFixed(2)}`} disabled />
                                    </div>
                                    <div>
                                      <Label>Método de Pagamento</Label>
                                      <select 
                                        className="w-full p-2 border rounded-md"
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                      >
                                        <option value="credit_card">Cartão de Crédito</option>
                                        <option value="debit_card">Cartão de Débito</option>
                                        <option value="pix">PIX</option>
                                        <option value="boleto">Boleto</option>
                                      </select>
                                    </div>
                                    <Button 
                                      onClick={() => handlePayment(payment.id)} 
                                      className="w-full bg-red-600 hover:bg-red-700"
                                      data-testid="confirm-payment-button"
                                    >
                                      Confirmar Pagamento (Mock)
                                    </Button>
                                    <p className="text-xs text-gray-500 text-center">
                                      *Este é um pagamento simulado para demonstração
                                    </p>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle>Documentos</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Upload Form */}
                <form onSubmit={handleDocumentUpload} className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold mb-4">Enviar Novo Documento</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="document_type">Tipo de Documento</Label>
                      <select name="document_type" className="w-full p-2 border rounded-md" required>
                        <option value="rg">RG</option>
                        <option value="cpf">CPF</option>
                        <option value="certidao_nascimento">Certidão de Nascimento</option>
                        <option value="comprovante_residencia">Comprovante de Residência</option>
                        <option value="atestado_medico">Atestado Médico</option>
                        <option value="foto">Foto 3x4</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="upload-document-button">
                        Enviar Documento (Mock)
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">*Upload simulado para demonstração</p>
                </form>

                {/* Documents List */}
                {documents.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Nenhum documento enviado</p>
                ) : (
                  <div className="space-y-3">
                    {documents.map(doc => (
                      <Card key={doc.id} className="border">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center">
                            <FileText className="mr-3 h-8 w-8 text-red-600" />
                            <div>
                              <p className="font-semibold capitalize">{doc.document_type.replace('_', ' ')}</p>
                              <p className="text-sm text-gray-500">
                                Enviado em {new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            doc.status === 'approved' ? 'bg-green-100 text-green-800' :
                            doc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {doc.status === 'approved' ? 'Aprovado' :
                             doc.status === 'rejected' ? 'Rejeitado' : 'Pendente'}
                          </span>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default ParentPortal;